import { brandLinka } from '@ads/tokens';
import { DashboardTickets } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { formatNumber } from '@linka/utils';
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../chart/chart';
import { ChartCard, ChartCardContent, ChartCardHeader, ChartCardTitle, ExportChartTriggerButton } from '../components';

interface SalesByTypeChartProps {
  data: DashboardTickets;
}
export const SalesByTypeChart = ({ data }: SalesByTypeChartProps) => {
  const t = useTranslation();

  const ticketsChartConfig = {
    totalSold: { label: t('participants'), color: brandLinka.baseColor.brand['400'] },
    availableStock: { label: t('remaining', { count: 2 }), color: brandLinka.contextualColor.interactive.primary },
  };
  return (
    <ChartCard>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.sold-vs-available-by-type')}</ChartCardTitle>
        <ExportChartTriggerButton type='OVERVIEW_TICKET' />
      </ChartCardHeader>

      {data.data.length === 0 ? (
        <ChartCardContent className='flex flex-1 items-center justify-center py-xl'>
          <span className='ts-paragraph-xs text-ctx-content-base'>{t('dashboard.charts.no-data')}</span>
        </ChartCardContent>
      ) : (
        <ChartCardContent className='flex'>
          <ChartContainer config={ticketsChartConfig} className='w-[60px]'>
            <BarChart responsive accessibilityLayer data={data.data} margin={{ top: 24 }} width={60}>
              <CartesianGrid vertical={false} />
              <Bar dataKey='totalSold' fill='transparent' barSize={0} />
              <YAxis
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                width={60}
                tickFormatter={(value) => formatNumber(value, { notation: 'compact' })}
              />
              <XAxis dataKey='label' height={120} />
            </BarChart>
          </ChartContainer>

          <div className='flex-1 overflow-x-auto pb-xs'>
            <ChartContainer config={ticketsChartConfig} className='w-full' dataLength={data.data.length}>
              <BarChart responsive accessibilityLayer data={data.data} margin={{ top: 24 }}>
                <CartesianGrid vertical={false} />
                <Bar dataKey='totalSold' stackId='a' fill='var(--color-totalSold)' radius={0}>
                  <LabelList
                    dataKey='totalSold'
                    position='insideTop'
                    className='mix-blend-difference text-base-neutral-white'
                  />
                </Bar>
                <Bar dataKey='availableStock' stackId='a' fill='var(--color-availableStock)' radius={[8, 8, 0, 0]}>
                  <LabelList dataKey='availableStock' position='top' />
                </Bar>
                <XAxis
                  dataKey='label'
                  tickLine={false}
                  tickMargin={24}
                  tick={{ textAnchor: 'start' }}
                  angle={45}
                  height={120}
                  tickFormatter={(label) => {
                    const labelLength = String(label).length;
                    const maxLength = 12;
                    return labelLength > maxLength ? `${String(label).substring(0, maxLength)}...` : label;
                  }}
                />
                <ChartTooltip
                  cursor={{ fill: brandLinka.contextualColor.interactive.secondary }}
                  content={<ChartTooltipContent />}
                />
              </BarChart>
            </ChartContainer>
          </div>
        </ChartCardContent>
      )}
    </ChartCard>
  );
};
